import type { ScoringPolarity } from './lib/status-bands';

export type { ScoringPolarity };

export type FilterSelection = Record<string, string[]>;

export interface TenantMembershipRecord {
  tenantId: string;
  tenantName: string;
  roles: string[];
  internalOnly?: boolean;  // Block access to /app/* routes for this tenant
  emailVerified?: boolean;
  /** Tenant-level fallback language (EL-694). Defaults to 'en'. */
  defaultLanguage?: string;
  /** Languages enabled for this tenant (EL-694). Defaults to ['en','nl']. */
  allowedLanguages?: string[];
}

export interface LoginResponse {
  userId: string;
  primaryTenantId: string;
  redirectPath: string;
  memberships: TenantMembershipRecord[];
  consentsGiven: string[];
  activeConsentVersion: string | null;
  userConsentVersion: string | null;
  needsReconsent: boolean;
}

export interface StoredSession {
  userId: string;
  primaryTenantId: string;
  redirectPath: string;
  memberships: TenantMembershipRecord[];
  consentsGiven: string[];
  activeConsentVersion: string | null;
  userConsentVersion: string | null;
  needsReconsent: boolean;
  preferredLanguage: string | null;
  accessibleLocations?: Array<{
    persona: string;
    path: string;
  }>;
  email?: string | null;
  displayName?: string | null;
}

// Membership Consent Types

/**
 * Consent types that can be given per membership
 * - platform_consent: GDPR data processing consent (full content display)
 * - privacy_policy: Privacy policy document (link-only display)
 * - terms_of_use: Terms of use document (link-only display)
 * - tos, privacy, data_processing: Legacy types (kept for backwards compatibility)
 */
export type MembershipConsentType = 'tos' | 'privacy' | 'data_processing' | 'platform_consent' | 'privacy_policy' | 'terms_of_use';

/**
 * New consent types supported by the platform (excluding legacy types)
 */
export type PlatformConsentType = 'platform_consent' | 'privacy_policy' | 'terms_of_use';

/**
 * Display mode for consent types
 * - full_content: Show full scrollable content (user must read)
 * - link_only: Show checkbox with link to view document
 */
export type ConsentDisplayMode = 'full_content' | 'link_only';

/**
 * All required consent types for new users
 */
export const REQUIRED_CONSENT_TYPES: MembershipConsentType[] = ['platform_consent', 'privacy_policy', 'terms_of_use'];

/**
 * Consent types that trigger re-consent when updated
 * Only platform_consent triggers re-consent; privacy_policy and terms_of_use don't block access on version change
 */
export const RECONSENT_TRIGGER_TYPES: MembershipConsentType[] = ['platform_consent'];

/**
 * Supported languages for consent
 */
export type ConsentLanguage = 'en' | 'nl' | 'fr';

/**
 * Single language consent text
 */
export interface PlatformConsentText {
  title: string;
  summary: string;
  fullText: string;  // HTML format (from WYSIWYG editor)
}

/**
 * Active platform consent from platform-service
 */
export interface ActivePlatformConsent {
  consentType: PlatformConsentType;
  displayMode: ConsentDisplayMode;
  version: string;
  status?: string;
  texts: {
    en?: PlatformConsentText;
    nl?: PlatformConsentText;
    fr?: PlatformConsentText;
  };
  publishedAt?: string | null;
}

/**
 * Human-readable labels for consent types
 */
export const CONSENT_TYPE_LABELS: Record<PlatformConsentType, string> = {
  platform_consent: 'Data Processing Consent',
  privacy_policy: 'Privacy Policy',
  terms_of_use: 'Terms of Use',
};

/**
 * URL slug mapping for consent types (used in /legal/:type routes)
 */
export const CONSENT_TYPE_SLUGS: Record<PlatformConsentType, string> = {
  platform_consent: 'data-processing',
  privacy_policy: 'privacy-policy',
  terms_of_use: 'terms-of-use',
};

/**
 * Create consent request payload
 */
export interface MembershipConsentCreateRequest {
  consent_type: MembershipConsentType;
  consent_version: string;
}

/**
 * Consent record response
 */
export interface MembershipConsentRecord {
  id: string;
  consentType: string;
  consentVersion: string;
  consented: boolean;
  consentedAt: string | null;
  declinedAt: string | null;
  withdrawnAt: string | null;
  createdAt: string;
}

/**
 * Onboarding milestones that can be tracked
 */
export interface OnboardingMilestones {
  profile_complete: boolean;
  bio_complete: boolean;
  first_scan: boolean;
  first_survey: boolean;
  viewed_wellbeing: boolean;
  security_configured: boolean;
}

/**
 * Onboarding progress for a user
 */
export interface OnboardingProgress {
  tour_completed: boolean;
  tour_skipped: boolean;
  tour_skipped_permanently: boolean;
  checklist_dismissed: boolean;
  security_prompt_dismissed: boolean;
  milestones: OnboardingMilestones;
  completed_at: string | null;
}

/**
 * User profile data returned from /users/me/profile
 */
export interface UserProfile {
  id: string;
  primary_email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  preferred_language: string | null;
  date_of_birth: string | null;  // ISO datetime string
  age: number | null;  // Calculated field
  height_cm: number | null;
  weight_kg: number | null;
  gender: string | null;  // 'male' | 'female' | 'other' | 'prefer_not_to_say'
  /** The login BEFORE this session's, ISO datetime string. `last_login_at` is
   *  overwritten by the current login on each request, so it can never answer
   *  "when did they last visit before now" — this field exists for that (see
   *  the greeting sub-line's since-clause, EL-1041). Absent (`null`) for a
   *  user on their first login or when the value predates this field.
   *  Optional (like `onboarding` below) so fixtures built before this field
   *  existed keep type-checking; treat a missing key the same as `null`. */
  previous_login_at?: string | null;
  onboarding?: OnboardingProgress;
  memberships: Array<{
    tenantId: string;
    tenantName: string;
    roles: string[];
    status?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  }>;
}

/**
 * Profile update request payload for PATCH /users/me/profile
 */
export interface ProfileUpdateRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  preferred_language?: string;  // ISO 639-1 code
  date_of_birth?: string;  // ISO format YYYY-MM-DD
  height_cm?: number;
  weight_kg?: number;
  gender?: string;  // 'male' | 'female' | 'other' | 'prefer_not_to_say'
}

/**
 * Onboarding update request payload for PATCH /users/me/onboarding
 */
export interface OnboardingUpdateRequest {
  tour_completed?: boolean;
  tour_skipped?: boolean;
  tour_skipped_permanently?: boolean;
  security_prompt_dismissed?: boolean;
  milestones?: Partial<OnboardingMilestones>;
}

/**
 * Form data structure for profile form
 */
export interface ProfileFormData {
  first_name: string;
  last_name: string;
  phone: string;
  preferred_language: string;  // Required
  date_of_birth: string;
  height_cm: string;  // String for input, converted to number on submit
  weight_kg: string;  // String for input, converted to number on submit
  gender: string;  // 'male' | 'female' | 'other' | 'prefer_not_to_say' | ''
}

/**
 * Validation errors for profile form fields
 */
export interface ProfileValidationErrors {
  first_name?: string;
  last_name?: string;
  phone?: string;
  preferred_language?: string;
  date_of_birth?: string;
  height_cm?: string;
  weight_kg?: string;
  gender?: string;
}

/**
 * Profile page tab types
 */
export type ProfileTabType = 'general' | 'bio' | 'consents' | 'security';

/**
 * Tenant membership type (extracted from StoredSession for reusability)
 */
export interface TenantMembership {
  tenantId: string;
  tenantName: string;
  roles: string[];
  internalOnly?: boolean;  // Block access to /app/* routes for this tenant
  emailVerified?: boolean;
  /** Tenant-level fallback language (EL-694). Defaults to 'en'. */
  defaultLanguage?: string;
  /** Languages enabled for this tenant (EL-694). Defaults to ['en','nl']. */
  allowedLanguages?: string[];
}

/**
 * Tenant context type for TenantContext provider
 */
export interface TenantContextType {
  currentTenant: TenantMembership | null;
  availableTenants: TenantMembership[];
  switchTenant: (tenantId: string) => Promise<void>;
  updateCurrentTenant: (updates: Partial<TenantMembership>) => void;
  loading: boolean;
  error: string | null;
}

/**
 * Switch tenant request payload
 */
export interface SwitchTenantRequest {
  tenant_id: string;
}

/**
 * Switch tenant response (same as LoginResponse — no token field,
 * authentication is cookie-based).
 */
export type SwitchTenantResponse = LoginResponse;

/**
 * Survey definition with SurveyJS schema and assessment configuration
 */
export interface Survey {
  id: string;
  title: string;
  description?: string;
  definition: Record<string, any>;  // SurveyJS JSON schema
  enable_assessments: boolean;
  assessment_rules: any[];
  permission_mode: 'all' | 'specific';
  allowed_tenant_ids: string[];
  required_tier_id?: string;
  status: string;
  version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * Survey deployment linking a survey to a tenant
 */
export interface Deployment {
  id: string;
  survey_id: string;
  survey_version: number;
  tenant_id: string;
  tenant_name: string;
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'expired' | 'archived';
  settings: {
    title: string;
    description?: string;
    [key: string]: any;
  };
  start_date?: string;  // ISO datetime string
  end_date?: string;  // ISO datetime string
  deployed_by: string;
  deployed_at: string;  // ISO datetime string
}

/**
 * Create deployment request payload
 */
export interface CreateDeploymentRequest {
  survey_id: string;
  tenant_id: string;
  tenant_name: string;
  settings: {
    title: string;
    description?: string;
    [key: string]: any;
  };
  start_date?: string;
  end_date?: string;
}

/**
 * Update deployment request payload
 */
export interface UpdateDeploymentRequest {
  settings?: {
    title?: string;
    description?: string;
    [key: string]: any;
  };
  start_date?: string;
  end_date?: string;
  status?: string;
}

/**
 * Survey list response from API
 */
export interface SurveyListResponse {
  surveys: Survey[];
  total: number;
}

/**
 * Deployment list response from API
 */
export interface DeploymentListResponse {
  deployments: Deployment[];
  total: number;
}

/**
 * Survey response (user's answers and assessment results)
 */
export interface SurveyResponse {
  id: string;
  deployment_id: string;
  tenant_id: string;
  respondent_id?: string;
  responses: Record<string, any>;
  metadata: Record<string, any>;
  status: 'in_progress' | 'completed' | 'abandoned';
  score?: number;
  assessment_category?: string;
  assessment_message?: string;
  domain_scores?: Record<string, number>;
  domain_assessments?: Record<string, { category: string; message: string }>;
  topic_scores?: Record<string, number>;
  topic_assessments?: Record<string, { category: string; message: string }>;
  started_at: string;
  completed_at?: string;
  updated_at: string;
}

/**
 * Response list from API
 */
export interface ResponseListResponse {
  responses: SurveyResponse[];
  total: number;
}

/**
 * Create/update response request payload
 */
export interface ResponseSubmitRequest {
  deployment_id: string;
  responses: Record<string, any>;
  metadata?: Record<string, any>;
}

// Messaging Types
export type PlatformType = 'teams' | 'teams-enterprise' | 'slack' | 'discord';

export type ConnectionStatus = 'connected' | 'pending' | 'error' | 'disconnected';

export interface MessagingConnection {
  id: string;
  tenant_id: string;
  platform: PlatformType;
  status: ConnectionStatus;
  bot_id: string;
  bot_name: string;
  service_url?: string;
  app_id?: string;
  webhook_url: string;
  error_message?: string;
  error_code?: string;
  conversation_count?: number;
  last_message_at?: string;
  connected_at: string;
  updated_at: string;
}

export interface ConnectTeamsRequest {
  microsoft_tenant_id: string;
  service_url?: string;
}

export interface ConnectTeamsEnterpriseRequest {
  app_id: string;
  app_password: string;
  microsoft_tenant_id: string;
  service_url?: string;
}

export interface TestConnectionRequest {
  connection_id: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: Record<string, any>;
}

export interface MessagingMessage {
  id: string;
  tenant_id: string;
  connection_id: string;
  platform: PlatformType;
  conversation_id: string;
  from_user_id: string;
  from_user_name: string;
  text: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface PaginatedMessages {
  items: MessagingMessage[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Dashboard Types

/**
 * Discriminated union for action card behaviors
 * Enables type-safe handling of navigation vs custom actions
 */
export type ActionCardAction =
  | { type: 'navigate'; path: string }
  | { type: 'custom'; handler: () => void | Promise<void> };

// Vitamins Integration Types

/**
 * Vitamins survey data point for radar chart
 */
export interface VitaminsRadarDataPoint {
  x: string;  // Category name (e.g., "GOAL", "SLEEP", "FOOD")
  y: number;  // Score value (0.0 to 1.0)
}

/**
 * Vitamins energy score with advice and color
 */
export interface VitaminsEnergyScore {
  score: number;          // Score value (0-10)
  advice: 'low' | 'medium' | 'high';
  color: string;          // Hex color code
}

/**
 * Vitamins questionnaire result
 */
export interface VitaminsQuestionnaireResult {
  name: string;
  date_completed: string;  // ISO datetime
  data: {
    total: VitaminsEnergyScore;
    physical_energy: VitaminsEnergyScore;
    mental_energy: VitaminsEnergyScore;
    spiritual_energy: VitaminsEnergyScore;
    emotional_energy: VitaminsEnergyScore;
  };
  radar: {
    categories: string[];
    series: VitaminsRadarDataPoint[][];  // Nested array: [[{x,y}, ...]]
  };
}

/**
 * Vitamins survey item from surveys list API
 */
export interface VitaminsSurvey {
  id: string;
  completion: number;      // 0.0 to 1.0
  isFinished: boolean;
  title?: string;
  submissionId?: string;   // Submission UUID from v2 API (needed for result endpoint)
}

/**
 * User state for survey engine surveys
 * - 'open': Survey is available and user hasn't started
 * - 'in_progress': User has started but not completed
 * - 'completed': User has completed the survey
 * - 'closed': Survey end_date has passed
 */
export type UserSurveyState = 'open' | 'in_progress' | 'completed' | 'closed';

/**
 * Survey engine survey item from user-list API
 * Native format (not vitamins-compatible)
 */
/** Survey status for assist admin view */
export type AssistSurveyStatus = 'draft' | 'published' | 'active' | 'completed' | 'archived' | 'scheduled' | 'in_progress' | 'ended';

/** Survey from eng-surveys endpoint for assist admin */
export interface AssistEngineSurvey {
  id: string;
  name: Record<string, string>;
  survey_type: 'standard' | 'pulse';
  status: AssistSurveyStatus;
  ui_status: string;
  start_date: string | null;
  end_date: string | null;
  participant_targeting: {
    type: 'all' | 'metadata' | 'user_list';
    metadata_filters?: Array<Record<string, unknown>>;
    user_ids?: string[];
  };
  auto_invite_new_matches?: boolean;
  anonymous_responses?: boolean;
  source_module_ids: string[];
  // Validation provenance inherited from the source template (EL-442); null for
  // ad-hoc surveys not created from a template.
  validation_status?: 'academically_validated' | 'market_standard' | null;
  source_url?: string | null;
  source_label?: string | null;
  created_at: string;
  updated_at: string;
  stats?: {
    total_responses: number;
    completion_rate: number;
  } | null;
}

export interface AssistSurveyListResponse {
  surveys: AssistEngineSurvey[];
  total: number;
}

export interface SurveyQuestionGroup {
  key: string;
  label: Record<string, string>;
  module_id?: string;
  questions: SurveyQuestion[];
}

export interface SurveyQuestion {
  key: string;
  text: Record<string, string>;
  response_type: string;
  disabled?: boolean;
}

export interface AssistEngineSurveyDetail extends AssistEngineSurvey {
  welcome_message?: Record<string, string>;
  internal_description?: string;
  question_groups: SurveyQuestionGroup[];
  descriptions?: Record<string, Record<string, string>> | null;
  source_type?: 'template' | 'modules';
  reminder_schedule?: number[];
  reminder_dates?: string[];
  estimated_duration_minutes?: number;
  disabled_question_keys?: string[];
  magic_link_enabled?: boolean;
  custom_invitation_subject?: Record<string, string> | null;
  custom_invitation_body?: Record<string, string> | null;
  custom_reminder_subject?: Record<string, string> | null;
  custom_reminder_body?: Record<string, string> | null;
}

export interface EngineSurvey {
  id: string;
  name: Record<string, string>;  // Multilingual: { en: "...", nl: "...", fr: "..." }
  survey_type: string;
  start_date: string | null;
  end_date: string | null;
  user_state: UserSurveyState;
  completion: number;           // 0.0 to 1.0
  submission_id: string | null;
  // Pulse survey fields (optional - only present for pulse surveys)
  pulse_frequency?: string | null;  // "weekly" | "bi_weekly" | "monthly" | "quarterly" | "custom"
  pulse_duration_days?: number | null;  // Duration of each pulse period in days
}

/**
 * Type guard to check if survey is an EngineSurvey (has user_state field)
 */
export function isEngineSurvey(survey: VitaminsSurvey | EngineSurvey): survey is EngineSurvey {
  return 'user_state' in survey;
}

/**
 * Condition expression for skip logic and field visibility
 */
export type ConditionExpr =
  | { kind: 'eq' | 'neq'; ref: string; value: string }
  | { kind: 'in' | 'not_in' | 'contains' | 'not_contains'; ref: string; values: string[] }
  | { kind: 'answered' | 'not_answered'; ref: string }
  | { kind: 'all' | 'any'; conditions: ConditionExpr[] };

/**
 * Answer value (scalar or multi-select array)
 */
export type AnswerValue = string | string[];

/**
 * Vitamins survey step answer option
 */
export interface VitaminsSurveyAnswer {
  value: string | number; // Answer value (used as identifier and for scoring)
  label: string; // Display text
  id?: string; // Optional UUID from API
  answer_scale_id?: string; // Optional scale reference
  sort?: number; // Optional sort order
  translations?: unknown[]; // Optional translation data
  is_correct?: boolean | null;
  media?: unknown | null;
  allow_other_text?: boolean; // Allow inline text input for "other" option
}

/**
 * API response structure from vitamins service (nested)
 */
export interface VitaminsApiStep {
  key: string;
  parent_key?: string | null;
  to?: Record<string, unknown>;
  meta: {
    kind?: string;
    show_buttons?: boolean;
    survey: string;
    title?: string;
    description?: string;
    questions: VitaminsApiQuestion[];
  };
}

export interface VitaminsApiQuestion {
  key: string;
  title: string;
  help?: string;
  answer_type: string;
  answers: VitaminsSurveyAnswer[];
  required: boolean;
  type: string;
  has_other?: boolean;
  condition?: ConditionExpr | null;
  media?: unknown | null;
}

/**
 * Flattened survey question/step for rendering
 */
export interface VitaminsSurveyStep {
  key: string;
  title: string;
  answer_type: string;
  answers: VitaminsSurveyAnswer[];
  required: boolean;
  type: string;
  condition?: ConditionExpr | null;
  group?: string;          // Question group identifier
  groupTitle?: string;     // Title for the group (from meta.title)
  groupDescription?: string; // Description for the group (from meta.description)
  scored?: boolean; // Whether this question contributes to scoring
  anchor_labels?: { low: Record<string, string>; high: Record<string, string> } | null; // Anchor labels for rating questions
}

/**
 * Dashboard survey with comprehensive data from BFF
 * Extends VitaminsSurvey with additional fields from v2 API
 */
export interface DashboardSurvey {
  id: string;
  completion: number;      // 0.0 to 1.0 (same as VitaminsSurvey)
  isFinished: boolean;
  title?: string;
  name?: string;           // Alias for title from v2 API
  description?: string;
  submissionId?: string;
  [key: string]: unknown;  // Additional vitamins-specific fields
}

/**
 * Pulse survey data for the dashboard
 * Used in the Weekly Check-in card when a pulse survey is configured
 */
export interface PulseSurveyData {
  id: string;
  completion: number;       // 0.0 to 1.0
  isFinished: boolean;
  originalTitle: string;    // Original template title (without date range)
  submissionId?: string;
}

/**
 * Survey state indicator for dashboard
 * - available: Survey is available for the user
 * - noSurveys: User has no surveys assigned at all
 * - completed: User has surveys but the configured dashboard survey isn't available (likely completed)
 * - closed: Survey exists but grace period has ended (grace_end passed)
 */
export type SurveyState = 'available' | 'noSurveys' | 'completed' | 'closed';

/**
 * Dashboard response from BFF /api/dashboard endpoint
 */
export interface DashboardResponse {
  survey: DashboardSurvey | null;
  surveyState: SurveyState;
  pulseSurvey: PulseSurveyData | null;
}

/**
 * Tenant settings model
 */
export interface TenantSettings {
  id: string;
  tenant_id: string;
  dashboard_survey_id: string | null;
  pulse_survey_id: string | null;
  pulse_frequency: string | null; // "weekly" | "bi-weekly" | "monthly" | "quarterly" | custom days
  pulse_duration_days: number | null;
  session_ttl_days?: number;
  impersonation_disabled?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Update tenant settings request
 */
export interface TenantSettingsUpdate {
  dashboard_survey_id?: string | null;
  pulse_survey_id?: string | null;
  pulse_frequency?: string | null;
  pulse_duration_days?: number | null;
  session_ttl_days?: number;
  impersonation_disabled?: boolean;
}

// Recommendation Types (SDT-based 5-driver model)

/**
 * Key drivers from Self-Determination Theory framework
 * - individual_health: Personal wellbeing (shown first)
 * - connection_trust: Relationships and belonging
 * - culture_purpose: Meaning and organizational alignment
 * - work_design_autonomy: Control and flexibility
 * - growth_mastery: Learning and competence
 */
export type KeyDriver =
  | 'connection_trust'
  | 'culture_purpose'
  | 'work_design_autonomy'
  | 'growth_mastery'
  | 'individual_health';

/**
 * Sub-factors for individual_health driver
 */
export type HealthSubFactor = 'physical' | 'mental_emotional' | 'environmental';

/**
 * Sub-factor analysis within individual_health
 */
export interface SubFactorAnalysis {
  sub_factor: HealthSubFactor;
  insights: string | null;
  areas_of_focus: string[];
}

/**
 * Source reference with URL and metadata
 */
export interface SourceReference {
  url: string;
  title: string | null;
  fetched_at: string | null;
}

/**
 * Recommendation for a single key driver
 */
export interface KeyDriverRecommendation {
  driver: KeyDriver;
  score: number | null;
  previous_score?: number | null;  // Previous score for trend calculation
  recommendation: string;
  rationale: string | null;
  sub_factors: SubFactorAnalysis[] | null;  // Only for individual_health
  sources: SourceReference[] | null;  // Optional list of source URLs with titles
  is_read: boolean;  // Whether user has expanded/read this recommendation
}

/**
 * Coaching difficulty level for SelfCare Growth Plan actions.
 * Frontend renders the label via i18n keys.
 */
export type CoachingLevel = 'beginner' | 'intermediate' | 'advanced';

/**
 * Recommendation for a single survey module (dynamic, from survey system)
 */
export interface ModuleRecommendation {
  module_id: string;
  module_name: Record<string, string>;  // Multilingual: { en: "Stress", nl: "Stress" }
  score: number | null;
  recommendation: string;
  rationale: string | null;
  sources: SourceReference[] | null;
  is_read: boolean;
  // SelfCare Growth Plan fields (all optional for backwards compatibility)
  action_title?: string | null;
  description?: string | null;
  reflect?: string | null;
  steps?: string[] | null;
  duration?: string | null;
  level?: CoachingLevel | null;
  expected_outcome?: string | null;
}

/**
 * Recommendation status during async processing
 */
export type RecommendationStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'no_survey_data';

/**
 * Full recommendation response from API
 */
export interface RecommendationResponse {
  id: string;
  user_id: string;
  tenant_id: string;
  language: string;
  status: RecommendationStatus;
  is_active: boolean;
  is_validated: boolean;
  key_drivers: KeyDriverRecommendation[] | null;
  module_recommendations: ModuleRecommendation[] | null;
  model_used: string | null;
  error_message: string | null;
  created_at: string;
  cached: boolean;
}

// Vitamins Survey Response Types (Your Responses view)

/**
 * Individual survey response item for "Your Responses" table
 */
export interface VitaminsSurveyResponseItem {
  question: string;
  subfactor: string;
  factor: string;
  answer: string;
  questionKey: string;
}

/**
 * Response from /api/vitamins/surveys/:surveyId/responses endpoint
 */
export interface VitaminsSurveyResponsesResponse {
  responses: VitaminsSurveyResponseItem[];
}

// Dashboard Insights Types (HES data transformed for display)

/**
 * Distribution percentages for score visualization
 */
export interface DashboardDistribution {
  very_negative: number;
  negative: number;
  neutral: number;
  positive: number;
  very_positive: number;
}

/**
 * Individual topic score within a domain
 */
export interface DashboardTopicScore {
  name: string;
  hes_factor: string;           // Original HES code (e.g., "SE", "SAFE")
  score: number;                // 0-10 scale
  trend: string | null;         // "up" | "down" | "stable" | null
  distribution: DashboardDistribution | null;
}

/**
 * Domain data with aggregated topics
 */
export interface DashboardDomainData {
  name: string;                 // Display name (e.g., "Connection & Trust")
  slug: string;                 // URL-safe identifier
  topics: DashboardTopicScore[];
  domain_score: number;         // Aggregated domain score (0-10)
}

/**
 * Full dashboard scores response from insights service
 */
export interface DashboardScoresResponse {
  survey_id: string;
  tenant_id: string;
  domains: DashboardDomainData[];
  overall_score: number;
  respondent_count: number;
  calculated_at: string;
}

/**
 * Categorized topics by score threshold
 * - action_required: score < 5
 * - monitoring: 5 <= score < 7
 * - on_track: score >= 7
 */
export interface DashboardCategorizedTopics {
  action_required: DashboardTopicScore[];
  monitoring: DashboardTopicScore[];
  on_track: DashboardTopicScore[];
}

// WellbeingWheel Types (Self-Care Overview)

/**
 * Individual category within a wellbeing segment
 */
export interface WellbeingCategory {
  id: string;       // Category key (e.g., 'GOAL', 'SLA', 'FOOD')
  name: string;     // Translated display name
  value: number;    // Score 0-100
}

/**
 * Wellbeing segment containing multiple categories
 */
export interface WellbeingSegment {
  id: string;               // Segment key: 'spiritual' | 'mental' | 'emotional' | 'physical'
  name: string;             // Translated display name
  mainValue: number;        // Domain energy score 0-100
  categories: WellbeingCategory[];
}

/**
 * Wellbeing wheel data from BFF /api/self-care/wellbeing/results
 */
export interface WellbeingWheelData {
  segments: WellbeingSegment[];
  totalScore: number;       // Overall wellbeing score 0-100
  completedAt: string;      // ISO datetime of completion
}

// Bulk Import Types

export type ImportJobStatus =
  | 'pending'
  | 'parsing'
  | 'validating'
  | 'importing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ImportJobProgress {
  totalRows: number;
  processedRows: number;
  currentPhase: string;
}

export interface ImportRowError {
  rowNumber: number;
  email: string;
  errors: string[];
}

export interface BulkImportResult {
  successCount: number;
  updatedCount: number;
  deactivatedCount: number;
  failedCount: number;
  /** Duplicate-email rows folded into one person during validation (EL-718). */
  duplicatesMerged?: number;
  /** Distinct team values across the imported rows (EL-718). */
  teamsCount?: number;
  createdUserIds: string[];
  updatedUserIds: string[];
  errors: ImportRowError[];
}

export interface ImportJob {
  id: string;
  tenantId: string;
  tenantName: string;
  createdByUserId: string;
  status: ImportJobStatus;
  progress: ImportJobProgress;
  result: BulkImportResult | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
}

export interface StartImportResponse {
  jobId: string;
  message: string;
}

// Evoluno Integration Types

/**
 * Evoluno configuration response (without sensitive data)
 */
export interface EvolunConfig {
  tenant_id: string;
  base_url: string;
  is_enabled: boolean;
  has_api_key: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Evoluno configuration create/update request
 */
export interface EvolunConfigRequest {
  api_key: string;
  base_url?: string;
}

/**
 * Evoluno connection test response
 */
export interface EvolunTestConnectionResponse {
  success: boolean;
  message: string;
  base_url: string;
}

/**
 * Evoluno sync status response
 */
export interface EvolunSyncStatus {
  total_users: number;
  matched_users: number;
  consent_synced: number;
  percentage: number;
}

/**
 * Evoluno user sync result
 */
export interface EvolunSyncResult {
  matched: number;
  not_found: number;
  skipped: number;
  failed: number;
  errors: string[];
}

/**
 * Evoluno consent sync result (single user or bulk)
 */
export interface EvolunConsentSyncResult {
  user_id?: string;  // Only present for single user sync
  synced: number;
  skipped: number;
  failed: number;
  errors: string[];
}

// HRIS Integration Types
// ==================

/** HRIS platform credential field schema */
export interface HrisCredentialField {
  name: string;
  label: string;
  type: string; // "text" | "password"
  required: boolean;
}

/** HRIS platform information with credential schema */
export interface HrisPlatformInfo {
  id: string;
  name: string;
  status: string; // "available" | "coming_soon"
  credentials: HrisCredentialField[];
  auth_mode: string; // "api_key" | "oauth"
  oauth_configured: boolean;
}

/** HRIS connection configuration request */
export interface HrisConfigRequest {
  platform: string;
  credentials: Record<string, string>;
  base_url?: string;
  company_id?: string;
  sync_employees?: boolean;
  sync_departments?: boolean;
  sync_absences?: boolean;
  auto_create_users?: boolean;
  sync_interval_minutes?: number;
}

/** HRIS connection configuration response (secrets masked) */
export interface HrisConfigResponse {
  tenant_id: string;
  platform: string;
  status: string; // "pending" | "connected" | "error" | "disabled"
  has_credentials: boolean;
  base_url: string | null;
  company_id: string | null;
  sync_enabled: boolean;
  sync_interval_minutes: number;
  sync_employees: boolean;
  sync_departments: boolean;
  sync_absences: boolean;
  auto_create_users: boolean;
  auth_mode: string;
  field_mapping_overrides: Record<string, string>;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  created_at: string;
  updated_at: string;
}

// Insights Dashboard Types (Survey Aggregation Engine)

/**
 * Question group score within a module
 */
export interface QuestionGroupScoreResponse {
  group_key: string;
  group_name: Record<string, string>;
  score: number | null;
  question_count: number;
  trend: 'up' | 'down' | 'stable' | null;
  scoring_polarity: ScoringPolarity;
}

export interface GroupScoreRef {
  group_key: string;
  group_name: Record<string, string>;
  score: number;
}

export interface PrimaryDriverRef {
  module_key: string;
  module_name: Record<string, string>;
  correlation: number | null;
  driver_kind?: 'module' | 'question_group';
  basis?: 'correlation' | 'score_gap';
  group_score?: number | null;
}

export interface TeamScoreRef {
  team_name: string;
  score: number;
}

export interface TeamBreakdownRef {
  team_name: string;
  score: number | null;
  category: 'action_required' | 'monitoring' | 'on_track' | null;
  strongest_group: GroupScoreRef | null;
}

/**
 * Aggregated score for one survey module
 */
export interface ModuleScoreResponse {
  module_key: string;
  module_name: Record<string, string>;
  score: number | null;
  respondent_count: number;
  group_scores: QuestionGroupScoreResponse[];
  trend: 'up' | 'down' | 'stable' | null;
  previous_score: number | null;
  category: 'action_required' | 'monitoring' | 'on_track' | null;
  module_type: 'kpi' | 'driver' | 'compliance_safety' | null;
  primary_driver: PrimaryDriverRef | null;
  attention_teams: TeamScoreRef[];
  positive_teams: TeamScoreRef[];
  team_breakdown: TeamBreakdownRef[];
}

/**
 * Full company insights response from insight service
 */
export interface CompanyInsightsResponse {
  tenant_id: string;
  overall_score: number | null;          // 0-100
  module_scores: ModuleScoreResponse[];
  total_respondents: number;
  calculated_at: string;                 // ISO datetime
  meets_threshold: boolean;              // Whether anonymity threshold is met
  /**
   * Whether the tenant's surveys configure any driver-type modules.
   * Used to distinguish "still calculating" from "survey has no drivers" on
   * the Overview page. Optional because legacy cached responses (computed
   * before the field existed) won't include it — call sites should default
   * missing values to `true` to preserve the legacy spinner UX.
   */
  has_driver_modules?: boolean;
  /** Mirror of has_driver_modules for kpi-type modules. */
  has_kpi_modules?: boolean;
}

/**
 * Cheap availability signal for the insights sidebar's "quick check".
 *
 * Served by the backend from a single point-read of the precomputed overview
 * doc (no aggregation), so the sidebar can decide whether results exist
 * without paying the full /insights/scores company aggregation.
 */
export interface InsightsAvailabilityResponse {
  has_results: boolean;
  /** See CompanyInsightsResponse.has_driver_modules — default true if absent. */
  has_driver_modules?: boolean;
  /** See CompanyInsightsResponse.has_kpi_modules — default true if absent. */
  has_kpi_modules?: boolean;
  /**
   * Whether the cached overview's pooled population meets the anonymity
   * threshold. Read alongside `computed_at`: `meets_threshold === false` only
   * signals "too few responses" when `computed_at` is set — otherwise it just
   * means the overview was never computed. Default false/absent if not sent.
   */
  meets_threshold?: boolean;
  /** ISO datetime the underlying snapshot was computed; null if never. */
  computed_at?: string | null;
  /**
   * This tenant's k-anonymity floor. Not part of the cached overview doc —
   * the backend pays one extra InsightConfig point-read for it so copy like
   * the Launched-stage locked-results card never hardcodes 5.
   */
  anonymity_threshold?: number;
}

/**
 * Workforce signal — prioritized issue detected from survey data
 */
export interface WorkforceSignal {
  module_key: string;
  module_name: Record<string, string>;
  group_key: string | null;
  group_name: Record<string, string> | null;
  score: number;                         // 0-100
  trend: 'up' | 'down' | 'stable' | null;
  priority_score: number;                // Composite score (low score + declining trend)
  category: 'action_required' | 'monitoring' | 'on_track';
}

/** Cross-sectional people pattern detected by nightly clustering job */
export interface PeoplePattern {
  id: string;
  module_key: string;
  module_name: Record<string, string>;
  segments: string[];
  dimension_fields: string[];
  score: number;
  org_score: number;
  member_count: number;
  name: Record<string, string>;
  description: Record<string, string>;
  insight: Record<string, string>;
  sentiment: 'concern' | 'watch' | 'positive';
}

/**
 * A single point in the participation trend history
 */
export interface TrendPoint {
  period: string;                        // YYYY-MM
  rate: number | null;                   // 0-1
}

/** Alias for TrendPoint scoped to participation */
export type ParticipationTrendPoint = TrendPoint;

/**
 * Breakdown by a single dimension (e.g., department)
 */
export interface ParticipationBreakdown {
  field: string;
  groups: ParticipationGroup[];
}

/**
 * Participation group within a breakdown dimension
 */
export interface ParticipationGroup {
  value: string;                         // e.g., "Engineering", "Brussels"
  eligible: number;
  completed: number;
  completion_rate: number;               // 0-1
  hidden: boolean;
}

/**
 * Most-recent survey summary within participation aggregate
 */
export interface ParticipationMostRecentSurvey {
  survey_id: string;
  name: string;
  completion_rate: number;
  completed: number;
  eligible: number;
  ended_at: string | null;
}

/**
 * Per-survey entry within participation aggregate
 */
export interface ParticipationPerSurveyEntry {
  survey_id: string;
  name: string;
  status: string;
  eligible: number;
  completed: number;
  completion_rate: number;
  ended_at: string | null;
}

/**
 * Aggregate participation data from monthly snapshot
 */
export interface ParticipationAggregate {
  participation_rate: number | null;
  participation_rate_trend: number | null;
  avg_per_survey_rate: number | null;
  engagement_depth_rate: number | null;
  total_participants: number;
  total_eligible: number;
  average_response_time_seconds: number | null;
  survey_count: number;
  breakdowns: ParticipationBreakdown[];
  trend_history: ParticipationTrendPoint[];
  most_recent_survey: ParticipationMostRecentSurvey | null;
  per_survey: ParticipationPerSurveyEntry[];
  privacy_hidden: boolean;
  empty_scope: boolean;
}

/**
 * Work status for action tracking
 */
export type WorkStatus = 'not_started' | 'in_progress' | 'completed';

/**
 * Action category based on score thresholds
 */
export type ActionCategory =
  | 'critical'
  | 'attention'
  | 'watch'
  | 'healthy'
  | 'thriving';

/**
 * Risk factor within an action (low-scoring group)
 */
export interface RiskFactor {
  group_key: string;
  group_name: Record<string, string>;
  score: number;
  trend: string | null;
  description: string;
}

/**
 * LLM-generated diagnosis text for a card.
 */
export interface Recommendation {
  description: string;
  what_we_found: string;
  why_it_matters: string;
  estimated_impact: string;
}

/**
 * Concrete action under a recommendation. 1–3 per card.
 */
export interface Action {
  title: string;
  description: string;
  effort: 'low' | 'medium' | 'high';
  impact: 'low' | 'medium' | 'high';
  timeframe: string;
}

/**
 * Person assigned to an action
 */
export interface Assignee {
  user_id: string;
  display_name: string;
  avatar_url: string | null;
}

/**
 * Progress note on an action
 */
export interface ProgressNote {
  id: string;
  type: 'update' | 'blocker' | 'milestone';
  text: string;
  author: string;
  created_at: string;
}

export type ActionSeverity =
  | 'critical'
  | 'attention'
  | 'watch'
  | 'healthy'
  | 'thriving';

export type ActionScope = 'org' | 'team';

/** V1 quadrant action type (EL-751): position on the score×leverage map. */
export type ActionType = 'invest' | 'protect' | 'monitor' | 'park';

export interface ActionSegment {
  field: string;
  values: string[];
}

export interface WorstTeamRef {
  field: string;
  value: string;
  score: number;
  sample_size: number;
}

/**
 * Single insight action (AI-generated recommendation with tracking)
 */
export interface InsightAction {
  skeleton_id: string;
  /**
   * The platform-admin consultant's curation flag (EL-706). Present on the wire;
   * only meaningful alongside `tenant_visibility_override` below — use
   * `isHiddenFromTeams` rather than reading either field alone.
   */
  show_to_tenant?: boolean;
  /**
   * The tenant admin's own visibility choice (EL-1064). `null`/absent means the
   * tenant has expressed nothing and the consultant's `show_to_tenant` governs.
   *
   * Two independent authorities, two fields, on purpose: sharing one would leave
   * the next curation pass unable to tell "the consultant published this" from
   * "the tenant un-hid it", and it would silently reverse the tenant's choice.
   */
  tenant_visibility_override?: boolean | null;
  severity: ActionSeverity;
  scope: ActionScope;
  segment: ActionSegment | null;
  module_key: string;
  module_name: Record<string, string>;
  group_key: string;
  group_name: Record<string, string>;
  score: number;
  previous_score: number | null;
  trend: string | null;
  risk_factors: RiskFactor[];
  recommendation: Recommendation;
  actions: Action[];
  affected_count: number;
  affected_teams: string[];
  // Optional: only org cards carry it, and not every InsightAction-shaped
  // object in the app (nav state, fixtures, partial responses) provides it.
  // The backend always sends it (defaults to []); consumers guard on absence.
  worst_teams?: WorstTeamRef[];
  history: Array<{ period: string; score: number }>;
  work_status: WorkStatus;
  assignees: Assignee[];
  due_date: string | null;
  percent_complete: number;
  progress_notes: ProgressNote[];
  scoring_polarity: ScoringPolarity;
  /** V1 quadrant label; null/absent on legacy cached docs. */
  action_type?: ActionType | null;
  /** V1 final ranking score [0,1]; null/absent on legacy cached docs. */
  final_score?: number | null;
}

/**
 * Response from insight actions endpoint
 */
export interface InsightActionsResponse {
  tenant_id: string;
  actions: InsightAction[];
  summary: {
    critical: number;
    attention: number;
    watch: number;
    healthy: number;
    thriving: number;
    total: number;
  };
  calculated_at: string;
  cached: boolean;
  /**
   * The survey wave these cards were computed from (EL-1138).
   *
   * The actions cache holds one doc per (tenant, language, filter) — always the
   * survey that was latest-completed at precompute time — and the read path
   * takes no survey parameter. Survey-pinned surfaces (segments) need this to
   * tell "no action targets this segment" apart from "these cards describe a
   * different wave". Absent on caches predating EL-1138 whose cards carry no
   * survey either; treat unknown as "cannot assert a mismatch".
   */
  survey_id?: string | null;
}

/**
 * One team's action cards plus its active-card count (EL-617).
 * `active_count` excludes cards marked completed — it's the number shown in
 * brackets in the scope dropdown and this team's size in the all-teams view.
 */
export interface InsightTeamActions {
  field: string;
  value: string;
  active_count: number;
  summary: {
    critical: number;
    attention: number;
    watch: number;
    healthy: number;
    thriving: number;
    total: number;
  };
  actions: InsightAction[];
}

/**
 * Response from the actions-by-team endpoint (EL-617).
 * `team_segment_field` is null for tenants with no real team dimension, in
 * which case `teams` is empty and the dropdown + team-view toggle are hidden.
 */
export interface InsightActionsByTeamResponse {
  tenant_id: string;
  team_segment_field: string | null;
  teams: InsightTeamActions[];
  status: 'ready' | 'pending';
}

/**
 * Update payload for action tracking
 */
export interface ActionTrackingUpdate {
  work_status?: WorkStatus;
  assignees?: Assignee[];
  due_date?: string | null;
  percent_complete?: number;
  progress_notes?: ProgressNote[];
}

/**
 * Response from action tracking PUT endpoint.
 * Contains the full persisted tracking state.
 */
export interface ActionTrackingResponse {
  work_status: WorkStatus;
  assignees: Assignee[];
  due_date: string | null;
  percent_complete: number;
  progress_notes: ProgressNote[];
}

/**
 * Filter dimension for insights (e.g., department, location)
 */
export interface InsightFilterDimension {
  field: string;
  display_name: string;
  values: string[];
  user_counts: Record<string, number>;
}

/** A filterable metadata field, identity only (no values). */
export interface FacetField {
  field: string;
  display_name: string;
}

/**
 * One distinct combination of metadata values across all fields.
 * `values` maps a field key to a scalar (single-value field) or a sorted list
 * (array field, e.g. tags); fields the member has no value for are omitted.
 * `count` is how many members share this exact combination.
 */
export interface FacetRow {
  values: Record<string, string | string[]>;
  count: number;
}

/**
 * The cross-filter "interlinking" payload: a compact joint distribution
 * (contingency table) of metadata values over the audience. Fetched once;
 * the frontend recomputes every dropdown's available values + counts from it
 * as filters change — no per-change server round-trip.
 */
export interface InsightFilterFacets {
  fields: FacetField[];
  rows: FacetRow[];
  total_members: number;
}

/** Snapshot of module scores at a point in time (from history endpoint) */
export interface InsightSnapshotResponse {
  id: string;
  tenant_id: string;
  scope: string;
  period: string;              // "YYYY-MM"
  period_start: string;        // ISO datetime
  period_end: string;          // ISO datetime
  overall_score: number | null;
  module_scores: ModuleSnapshotScoreResponse[];
  total_respondents: number;
  meets_threshold: boolean;
  created_at: string;
  updated_at: string;
}

/** HRIS connection test result */
export interface HrisTestConnectionResponse {
  success: boolean;
  message: string;
  platform: string;
  employee_count: number | null;
}

/** Response after triggering an HRIS sync */
export interface HrisSyncTriggerResponse {
  job_id: string;
  status: string;
  message: string;
}

/** Current HRIS sync status summary */
export interface HrisSyncStatusResponse {
  connected: boolean;
  platform: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  cached_employees: number;
  matched_employees: number;
}

/** HRIS sync job detail */
export interface HrisSyncJob {
  id: string;
  platform: string;
  job_type: string; // "full" | "delta" | "manual"
  status: string; // "running" | "completed" | "partial" | "failed"
  triggered_by: string | null;
  total_records: number;
  processed_records: number;
  created_count: number;
  updated_count: number;
  deactivated_count: number;
  skipped_count: number;
  failed_count: number;
  errors: string[];
  started_at: string;
  completed_at: string | null;
}

/** Paginated list of HRIS sync jobs */
export interface HrisSyncJobListResponse {
  data: HrisSyncJob[];
  page: number;
  page_size: number;
  total: number;
}

/** Cached HRIS employee data */
export interface HrisEmployee {
  id: string;
  tenant_id: string;
  hris_employee_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  employment_status: string;
  department: string | null;
  team: string | null;
  job_title: string | null;
  location: string | null;
  cost_center: string | null;
  manager_hris_id: string | null;
  hire_date: string | null;
  termination_date: string | null;
  elli_user_id: string | null;
  last_synced_at: string;
}

/** Paginated list of HRIS employees */
export interface HrisEmployeeListResponse {
  data: HrisEmployee[];
  page: number;
  page_size: number;
  total: number;
}

/** Cached HRIS department */
export interface HrisDepartment {
  id: string;
  tenant_id: string;
  name: string;
  parent_department_id: string | null;
  cost_center: string | null;
}

export interface ModuleSnapshotScoreResponse {
  module_key: string;
  module_name: Record<string, string>;
  score: number | null;
  respondent_count: number;
  category: 'action_required' | 'monitoring' | 'on_track' | null;
  group_scores: Array<{
    group_key: string;
    group_name: Record<string, string>;
    score: number | null;
    question_count: number;
  }>;
}

// Aggregate Participation Types (Dashboard)

/* TrendPoint defined above */

/**
 * Group within an aggregate breakdown dimension
 */
export interface AggregateBreakdownGroup {
  value: string;
  eligible: number;
  completed: number;
  completion_rate: number;
  hidden: boolean;
}

/**
 * Breakdown by a single dimension (aggregate across surveys)
 */
export interface AggregateBreakdown {
  field: string;
  groups: AggregateBreakdownGroup[];
}

// Team Risk Types (Pre-computed team risk summary)

export type SeverityLevel = 'high' | 'elevated' | 'moderate' | 'low';

/**
 * Single driver-KPI correlation within a team scope
 */
export interface DriverCorrelation {
  kpi_module_key: string;
  kpi_module_name: Record<string, string>;
  driver_module_key: string;
  driver_module_name: Record<string, string>;
  correlation: number | null; // null for score_gap entries
  sample_size: number;
  rank: number;
  driver_kind?: 'module' | 'question_group';
  basis?: 'correlation' | 'score_gap';
  group_score?: number | null;
}

/**
 * Team correlations response — KPI-driver relationships scoped to a team
 */
export interface TeamCorrelationsResponse {
  tenant_id: string;
  scope: string; // "{groupBy}_{teamValue}"
  kpi_correlations: Record<string, DriverCorrelation[]>;
  computed_at: string;
}

/**
 * Team detail response — full team context with insights and member count
 */
export interface TeamDetailResponse {
  tenant_id: string;
  group_by: string;
  team_value: string;
  member_count: number;
  last_survey_date: string | null;
  insights: CompanyInsightsResponse;
  warning: string | null;
  calculated_at: string;
}

export interface ModuleScoreCompact {
  module_key: string;
  module_name: Record<string, string>;
  score: number | null;
  severity: SeverityLevel | null;
}

export interface TeamRiskEntry {
  team_name: string;
  group_by: string;
  member_count: number;
  overall_score: number | null;
  meets_threshold: boolean;
  top_risk_module_key: string | null;
  top_risk_module_name: Record<string, string>;
  top_risk_score: number | null;
  top_risk_severity: SeverityLevel | null;
  module_scores: ModuleScoreCompact[];
  last_survey_date: string | null;
  division: string | null;
}

export interface TeamRiskResponse {
  id: string;
  tenant_id: string;
  group_by: string;
  teams: TeamRiskEntry[];
  ai_summary: Record<string, string> | null;
  computed_at: string;
}

// Radar Chart Types (Survey Engine)

/**
 * Group score with metadata for radar chart visualization
 */
export interface GroupScoreWithMeta {
  groupKey: string;
  groupTitle: Record<string, string>;
  score: number;
  sourceModuleId: string | null;
}

/**
 * Module-level aggregated score with nested group scores
 */
export interface ModuleScoreAggregate {
  moduleId: string;
  moduleName: Record<string, string>;
  score: number;
  groups: GroupScoreWithMeta[];
}

/**
 * Radar chart data for survey engine results visualization
 */
export interface RadarChartData {
  id: string;
  surveyId: string;
  totalScore: number | null;
  moduleScores: ModuleScoreAggregate[];
  completedAt: string | null;
}

// ---------------------------------------------------------------------------
// Drivers Heatmap (GET /api/insights/drivers)
// ---------------------------------------------------------------------------

/** Five-band status classification shared with the insights backend. */
export type DriversStatusBand =
  | 'thriving'
  | 'healthy'
  | 'watch'
  | 'attention'
  | 'critical';

/** One cell in the drivers heatmap: a single driver × group_by column value. */
export interface DriversMatrixCell {
  score: number | null; // null when hidden by anonymity threshold
  respondent_count: number;
  status: DriversStatusBand | null;
  previous_score: number | null;
  scoring_polarity: ScoringPolarity;
}

/** One question asked within a driver's question group (text only). */
export interface DriverQuestion {
  key: string;
  title: Record<string, string>;
}

/** One row of the heatmap: a driver (question group) across all columns. */
export interface DriverRow {
  group_key: string;
  group_name: Record<string, string>;
  /** Optional i18n group description, used for the hover info box. */
  description?: Record<string, string> | null;
  cells: DriversMatrixCell[];
  scoring_polarity: ScoringPolarity;
  /** Scored questions asked in this group, for the heatmap hover. */
  questions?: DriverQuestion[];
}

export type CategoricalTrend = 'improving' | 'stable' | 'declining' | null;

/**
 * One option tally on a categorical question.
 *
 * `previous_percentage` and `trend` are only populated when the matrix
 * was loaded with a `compare_with` argument; in snapshot mode both are
 * `null`.
 */
export interface CategoricalOption {
  value: string;
  label: Record<string, string>;
  count: number;
  percentage: number;
  previous_percentage: number | null;
  trend: CategoricalTrend;
  trend_value: number | null;
}

/** One verbatim free-text answer surfaced for a flagged (RIE) question. */
export interface CategoricalFreeText {
  option_value: string;
  label: Record<string, string>;
  text: string;
}

/**
 * A non-scored question rendered below the heatmap for one domain.
 *
 * Tenant-wide aggregate from the latest survey containing the question.
 * Aggregates below the tenant's anonymity threshold are omitted by the
 * backend (except groups flagged `surface_free_text`, which are exempt).
 */
export interface CategoricalQuestion {
  group_key: string;
  question_key: string;
  question_title: Record<string, string>;
  answer_type: string;
  survey_id: string;
  total_respondents: number;
  options: CategoricalOption[];
  open_text_count: number;
  surface_free_text: boolean;
  open_texts: CategoricalFreeText[];
}

/** One module (domain) block: header metadata plus its driver rows. */
export interface DomainBlock {
  module_key: string;
  module_name: Record<string, string>;
  score: number | null;
  previous_score: number | null;
  status: DriversStatusBand | null;
  /**
   * Wire value is the backend's `DriversTrend` — `'improving' | 'stable' |
   * 'declining'` (see `drivers_matrix.py`) — not `'up' | 'down' | 'stable'`.
   * Was mistyped as the latter (EL-1047): every consumer that branched on it
   * fell through to "stable" regardless of actual direction, so the
   * heatmap's comparison view never showed a trend arrow. Fixed by reusing
   * the `CategoricalTrend` alias, matching the `column_trends` sibling
   * below. Render sites must map this to the icon vocabulary themselves —
   * see `trendDirection()` in `hooks/useModuleDeltas.ts`.
   */
  trend: CategoricalTrend;
  trend_value: number | null;
  /**
   * Per-column domain score, aligned index-for-index with
   * {@link DriversMatrixResponse.columns}. Same weighted mean as `score`
   * but partitioned by column, over the SAME visible cells — k-anonymity
   * applies identically. `null` for a column with no visible cell in this
   * domain; never 0. Empty array on legacy cached payloads written before
   * the field existed.
   */
  column_scores: (number | null)[];
  /**
   * Per-column `previous_score`, aligned with
   * {@link DriversMatrixResponse.columns}. Columns are paired with the
   * comparison snapshot by NAME server-side, not by position — the
   * comparison's column set can be a strict subset. `null` for a column
   * with no match. Empty array when no `compare_with` was requested.
   */
  column_previous_scores: (number | null)[];
  /**
   * Per-column trend vs the comparison snapshot, aligned with
   * {@link DriversMatrixResponse.columns}. A column's trend is independent
   * of the org-wide `trend` and may point the other way — that is the
   * point of the field. Empty array when no `compare_with` was requested.
   */
  column_trends: CategoricalTrend[];
  /**
   * Per-column signed delta (column score - comparison column score),
   * aligned with {@link DriversMatrixResponse.columns}. Sign is raw, NOT
   * polarity-corrected: read direction from `column_trends`, magnitude
   * from here. Empty array when no `compare_with` was requested.
   */
  column_trend_values: (number | null)[];
  drivers: DriverRow[];
  /**
   * Non-scored diagnostic / follow-up questions for this module, ready
   * for the open-answers panel. Empty when the module has no such
   * questions or when none met the anonymity threshold.
   */
  categorical_questions: CategoricalQuestion[];
}

/** A raw column value that was pooled into the synthetic "Other"/"Organization" column. */
export interface MergedBucket {
  /** Original column value. `null` means "no value for this field". */
  name: string | null;
  /** Distinct respondents from this bucket included in 'Other'. */
  count: number;
}

/** Optional per-column sidecar surfaced by the backend. */
export interface ColumnMetadata {
  /**
   * Buckets that were pooled into this synthetic column (display order).
   * `null` or absent when the column is not synthetic.
   */
  merged_buckets?: MergedBucket[] | null;
}

/**
 * Response from `GET /api/insights/drivers` — the full drivers heatmap.
 *
 * `columns` are column header values in the same order cells appear inside
 * each {@link DriverRow}. When a tenant has no metadata fields the backend
 * returns a single `['Organization-wide']` column fallback.
 */
export interface DriversMatrixResponse {
  tenant_id: string;
  group_by: string;
  columns: string[];
  domains: DomainBlock[];
  kpi_domains: DomainBlock[];
  /**
   * Whether the tenant's surveys configure any driver-type modules. When
   * false the drivers section should render an empty state instead of the
   * "Calculating your insights" spinner — there's nothing to wait for.
   * Optional because legacy cached payloads (written before the field
   * existed) won't include it. Call sites default missing values to `true`
   * to preserve the legacy spinner UX.
   */
  has_driver_modules?: boolean;
  /** Mirror of has_driver_modules for the KPI section. */
  has_kpi_modules?: boolean;
  available_filters: InsightFilterDimension[];
  calculated_at: string; // ISO datetime
  meets_threshold: boolean;
  /**
   * Per-column sidecar keyed by the column value. Only the synthetic
   * "Other"/"Organization" column has an entry; other columns are absent.
   * Optional because legacy cached payloads (written before this field
   * existed) won't include it.
   */
  column_metadata?: Record<string, ColumnMetadata>;
}

export type DriversTrendView = 'snapshot' | 'evolution' | 'change';

export interface ComparableSurveyOption {
  id: string; // "previous" | "baseline" | <survey_id>
  kind: 'previous' | 'baseline' | 'survey';
  label: Record<string, string>;
  completed_at: string | null; // ISO 8601 or null
  resolved_survey_id: string | null;
  /** Distinct respondents; null on the synthetic previous/baseline aliases. */
  respondent_count?: number | null;
}

export interface ComparableSurveysResponse {
  tenant_id: string;
  options: ComparableSurveyOption[];
}

export interface CategoricalEvolutionOption {
  value: string;
  label: Record<string, string>;
  count: number;
  percentage: number;
}

export interface CategoricalEvolutionPoint {
  survey_id: string;
  survey_name: Record<string, string>;
  completed_at: string;
  total_respondents: number;
  /**
   * Empty when total_respondents was below the anonymity threshold —
   * the frontend should break the stacked-area band at this point.
   */
  options: CategoricalEvolutionOption[];
  open_text_count: number;
}

export interface CategoricalEvolutionQuestion {
  group_key: string;
  group_title: Record<string, string>;
  question_key: string;
  question_title: Record<string, string>;
  answer_type: string;
  time_series: CategoricalEvolutionPoint[];
}

export interface CategoricalEvolutionDomain {
  module_key: string;
  module_name: Record<string, string>;
  questions: CategoricalEvolutionQuestion[];
}

export interface CategoricalEvolutionResponse {
  tenant_id: string;
  domains: CategoricalEvolutionDomain[];
  calculated_at: string;
}

export interface EvolutionPoint {
  survey_id: string;
  survey_name: Record<string, string>;
  completed_at: string;
  score: number | null;
  respondent_count: number;
}

export interface EvolutionDriver {
  group_key: string;
  group_name: Record<string, string>;
  scoring_polarity: 'positive' | 'negative';
  time_series: EvolutionPoint[];
}

export interface EvolutionDomain {
  module_key: string;
  module_name: Record<string, string>;
  drivers: EvolutionDriver[];
}

export interface DriversEvolutionResponse {
  tenant_id: string;
  domains: EvolutionDomain[];
  kpi_domains: EvolutionDomain[];
  calculated_at: string;
}

/**
 * A single glossary term (EL-569). Sourced from the multilingual academic
 * `definition` fields on platform modules + their sub-constructs (EL-293),
 * flattened by the app BFF `GET /api/glossary` endpoint.
 */
export interface GlossaryEntry {
  /** Stable id: the module id, or `${moduleId}:${groupKey}` for a driver. */
  id: string;
  category: 'module' | 'driver';
  /** Multilingual display term ({en, nl, fr}). */
  term: Record<string, string>;
  /** Multilingual definition ({en, nl, fr}). */
  definition: Record<string, string>;
  /** Module slug/key (own slug for a module, parent slug for a driver). */
  moduleKey: string;
  /** Localized parent-module name, shown as context on driver rows. */
  moduleName?: Record<string, string>;
  /** For drivers: the question-group key (matches DriverRow.group_key). */
  groupKey?: string;
  /** Source instrument (e.g. UWES-9, BAT) — a lightweight framework hint. */
  sourceInstrument?: string | null;
  validationStatus?: string | null;
}

export interface GlossaryResponse {
  entries: GlossaryEntry[];
  total: number;
}

// ===========================================================================
// Correlations page (EL-722)
// ===========================================================================

/** Significance band for a correlation cell, derived from BOTH r and n. */
export type SignificanceBand = 'p_lt_01' | 'p_lt_05' | 'ns';

/** Display band for |r|. Classified by the backend, never in the browser. */
export type CorrelationStrength = 'strong' | 'moderate' | 'weak';

/**
 * Whether a link is GOOD for the outcome — sign of r combined with the column's
 * `scoring_polarity`. Drives the cell colour; the printed +/- sign carries the
 * direction. Classified by the backend.
 */
export type CellValence = 'favourable' | 'unfavourable' | 'neutral';

/**
 * One driver x KPI intersection in the correlation matrix.
 *
 * `measured: false` means no correlation is available — below the anonymity
 * threshold, or absent from the stored feed. It is NOT the same as r = 0 and
 * must render as an explicit "not measured" state.
 */
export interface CorrelationMatrixCell {
  kpi_group_key: string;
  measured: boolean;
  correlation: number | null;
  sample_size: number | null;
  p_value: number | null;
  significance: SignificanceBand | null;
  strength: CorrelationStrength | null;
  valence: CellValence | null;
}

/** The tightest measured driver-KPI pair, served with the matrix. */
export interface CorrelationStrongestLink {
  driver_group_key: string;
  driver_name: string;
  kpi_group_key: string;
  kpi_name: string;
  correlation: number;
  strength: CorrelationStrength;
  sample_size: number;
}

/** One KPI question-group column. */
export interface CorrelationMatrixKpiColumn {
  group_key: string;
  module_key: string;
  name: string;
  module_name: string;
  kpi_score: number | null;
  /** 'negative' = lower is better (e.g. burnout risk). Drives the direction affordance. */
  scoring_polarity: 'positive' | 'negative';
}

/**
 * GET /api/insights/narrative — the shared "Elli analysis" band (EL-730).
 *
 * `status='computing'` means another request is already generating this
 * exact view; the caller polls. `status='unavailable'` covers everything
 * else that isn't a paragraph — nothing worth saying, the daily generation
 * cap, or a generation failure — and the frontend renders all three
 * non-ready statuses identically: no band, never an error.
 */
export interface PageNarrativeResponse {
  status: 'ready' | 'computing' | 'unavailable';
  text?: string | null;
  generated_at?: string | null;
}

/** One driver question-group row; `cells` is in the same order as `kpi_columns`. */
export interface CorrelationMatrixDriverRow {
  group_key: string;
  module_key: string;
  name: string;
  module_name: string;
  driver_score: number | null;
  cells: CorrelationMatrixCell[];
}

/** GET /api/insights/correlations/matrix */
export interface CorrelationMatrixResponse {
  tenant_id: string;
  scope: string;
  survey_id: string;
  kpi_columns: CorrelationMatrixKpiColumn[];
  driver_rows: CorrelationMatrixDriverRow[];
  /** Backend-selected tightest pair; null when nothing is measured. */
  strongest_link: CorrelationStrongestLink | null;
  /** The |r| cut-points behind each cell's `strength`. Interpolate into legend copy. */
  strength_thresholds: { strong: number; moderate: number };
  /** Never hardcode "5" in threshold copy — interpolate this. */
  anonymity_threshold: number;
  computed_at: string;
}

/** Quadrant label for a driver. */
export type QuadrantActionType = 'invest' | 'protect' | 'monitor' | 'park';

/** One driver plotted on the score x leverage map. */
export interface QuadrantPoint {
  group_key: string;
  module_key: string;
  name: string;
  module_name: string;
  /** x-axis, 0-100. */
  score: number;
  /** y-axis, 0-1: shared leverage L, or |r| for the selected KPI lens. */
  leverage: number;
  /** leverage x (100 - score). */
  priority: number;
  action_type: QuadrantActionType;
  is_key_driver: boolean;
}

export interface QuadrantCounts {
  invest: number;
  protect: number;
  monitor: number;
  park: number;
}

/** GET /api/insights/quadrant */
export interface QuadrantMapResponse {
  tenant_id: string;
  scope: string;
  survey_id: string;
  /** null = the combined shared-leverage lens. */
  kpi_group_key: string | null;
  tau: number;
  points: QuadrantPoint[];
  /** Vertical line on the 0-100 scale. */
  score_threshold: number;
  /** Horizontal line, 0-1. */
  leverage_threshold: number;
  counts: QuadrantCounts;
}

// ---------------------------------------------------------------------------
// Workforce segments (EL-722)
// ---------------------------------------------------------------------------

/** LLM-generated copy per locale. Empty until the naming step has run. */
export interface SegmentText {
  en: string;
  nl: string;
  fr: string;
}

export type PriorityBand = 'high' | 'medium' | 'maintain';

export interface SegmentDriverStanding {
  group_key: string;
  /** Display title per locale; falls back to the key when a survey lacks one. */
  name: Record<string, string>;
  /** Parent driver module per locale — the deck's "Vier categorieën". */
  category: Record<string, string>;
  /** Banded on deviation, NOT on `priority`: how far behind, not where the
   * leverage is. The card shows both. */
  band: PriorityBand;
  /** Segment mean, 0-100. */
  score: number;
  /** (segment mean − org mean) / org sd. */
  deviation: number;
  priority: number;
  /** A_g against each KPI module, so the anchor selector re-ranks without a
   * fetch. Empty on documents written before EL-731. */
  priority_by_kpi?: Record<string, number>;
}

export type SegmentStatus =
  | 'requires_focus'
  | 'worth_monitoring'
  | 'strength'
  | 'hidden_risk'
  | 'quiet_strength';

export interface PersistedSegment {
  key: string;
  /** 1 = strongest on the anchor KPI. */
  rank: number;
  size: number;
  /** 0-1 of the segmented population. */
  share: number;
  status: SegmentStatus;
  /** Every KPI module, keyed by module id — the anchor switches client-side. */
  kpi_scores: Record<string, number>;
  drivers: SegmentDriverStanding[];
  /** KPI module id -> driver group keys in display order for that anchor. */
  driver_order_by_kpi?: Record<string, string[]>;
  /** KPI module id -> the (drivers × KPI) quadrant for that anchor. */
  status_by_kpi?: Record<string, SegmentStatus>;
  /** Where these people sit in the org. Empty when the tenant has no team
   * metadata, or when every cell is below the anonymity threshold. */
  teams: SegmentTeamShare[];
  /** Traits this segment shares that the company as a whole does not. Fields
   * are whatever the tenant records — never a fixed taxonomy. */
  profile: SegmentProfileChip[];
  name: SegmentText | null;
  subtitle: SegmentText | null;
  narrative: SegmentText | null;
}

/** The org-wide span of one driver, for the compare view's bars. */
export interface DriverScale {
  min: number;
  max: number;
  mean: number;
}

/** One "who resembles who" chip: a trait most of a segment shares. */
export interface SegmentProfileChip {
  /** Metadata field name, as the tenant records it. */
  field: string;
  value: string;
  /** 0-1 of this segment's members who carry the value. */
  share: number;
}

/** One row of a segment's "where they are" breakdown. */
export interface SegmentTeamShare {
  /** Team value as the tenant records it; empty on the pooled row, whose
   * label is display copy and lives in the locale files. */
  name: string;
  size: number;
  /** 0-1 of this segment's placeable members. */
  share: number;
  /** The sub-threshold remainder, not a real team. */
  pooled: boolean;
}

/** One respondent on the map. Coordinates only — carries no identifier. */
export interface SegmentPoint {
  x: number;
  /** KPI module id -> this respondent's score. One per KPI so the anchor
   * selector can re-project the map instead of freezing every dot. */
  scores: Record<string, number>;
  segment_key: string;
}

export interface SegmentQuality {
  silhouette: number;
  distinctness: number;
  feature_count: number;
  feature_keys: string[];
  renderable: boolean;
}

export interface SegmentMovement {
  previous_survey_id: string | null;
  comparable: boolean;
  joined: number;
  left: number;
  stayed: number;
  moved: number;
  matrix: Record<string, number>;
}

/** Why a wave produced no segments — served instead of a bare empty state. */
export interface SegmentsUnavailable {
  /**
   * Machine-readable cause from `segment_routes.py`. Two families, worded
   * differently because only one is actionable: data-volume/quality
   * (`too_few_respondents`, `indistinguishable_from_chance`,
   * `segments_too_similar`, `below_quality_floor`) versus structural
   * (`no_driver_modules`, `no_kpi_modules`) — a questionnaire missing either
   * kind of question can never be segmented, however long anyone waits.
   */
  reason: string;
  /** Only meaningful when `reason` is population-related; null for quality withholding. */
  respondents: number | null;
  /**
   * Comes from the backend, never computed here: it is the smallest allowed
   * number of segments times THIS tenant's anonymity threshold, so a hardcoded
   * number would be wrong for any tenant that raises its threshold. Null for
   * quality withholding — a wave withheld for that reason had plenty of people.
   */
  required_respondents: number | null;
}

export interface SegmentsResponse {
  tenant_id: string;
  scope: string;
  survey_id: string;
  segments: PersistedSegment[];
  points: SegmentPoint[];
  kpi_ids: string[];
  /** KPI module id -> display title per locale. */
  kpi_names: Record<string, Record<string, string>>;
  /** KPI module id -> 'positive' | 'negative'; needed to re-rank on anchor switch. */
  kpi_polarity: Record<string, string>;
  anchor_kpi_id: string | null;
  /** The "Elli analysis" band: one generated paragraph per locale, or null
   *  before the naming step has run. */
  analysis: SegmentText | null;
  /** Driver group key -> the org-wide span the compare bars are drawn on.
   *  EL-728: lowest→highest with the average at its true position. */
  driver_scale: Record<string, DriverScale>;
  /** Driver group key -> 'positive' | 'negative'. Scores are raw, so the bars
   *  need this to point the right way. */
  driver_polarity: Record<string, string>;
  /** Metadata field the team breakdown is grouped by (department, team, …). */
  team_field: string | null;
  quality: SegmentQuality | null;
  movement: SegmentMovement | null;
  /** Present only when the wave produced NO segments, and says why. */
  unavailable: SegmentsUnavailable | null;
  anonymity_threshold: number;
  excluded_respondents: number;
  computed_at: string;
}

// ---------------------------------------------------------------------------
// Home-page preferences (favourites, dismissed sections, recently viewed)
// ---------------------------------------------------------------------------

/**
 * Which reading of a page the user had open (EL-1065). A closed set, mirroring
 * `HomeViewLens` in `libs/python/core/src/core/models/user.py` — the backend
 * rejects anything else at the model boundary.
 */
export type HomeViewLens = 'kpi' | 'driver' | 'segment' | 'team';

/**
 * What the user did INSIDE a page, for a richer recently-viewed subtitle.
 *
 * Two structured fields, no scope name. The mockup shows one ("Sales
 * Commercial"), but a team or segment name is tenant free-text and this record
 * is behavioural telemetry about a named user — the lens and the count carry
 * the useful part of the sentence without storing any of that.
 */
export interface HomeRecentlyViewedDetail {
  lens: HomeViewLens;
  /** Items opened or compared under that lens. Bounded 0-99 server-side. */
  count: number;
}

/** A single recently-viewed insights page for the home page's list. Mirrors
 *  `HomeRecentlyViewedEntryResponse` in `apps/backends/user/src/api/v1/routes.py`. */
export interface HomeRecentlyViewedEntry {
  key: string;
  viewed_at: string;
  /** Absent on entries written by an older client and on pages with no lens
   *  concept — such a row renders exactly as it always did, never with an
   *  empty clause. */
  detail?: HomeRecentlyViewedDetail | null;
}

/** The current user's home-page preferences, scoped server-side to their
 *  current tenant. Mirrors `HomePreferencesResponse` (same routes file). */
export interface HomePreferences {
  favourites: Record<string, boolean>;
  dismissed: Record<string, boolean>;
  recently_viewed: HomeRecentlyViewedEntry[];
}

/** Partial-update payload for `PATCH /users/me/home-preferences`. Mirrors
 *  `UpdateHomePreferencesRequest` — `favourites`/`dismissed` are shallow-merged
 *  server-side; `recently_viewed_key` records a visit (capped server-side). */
export interface UpdateHomePreferencesPayload {
  favourites?: Record<string, boolean>;
  dismissed?: Record<string, boolean>;
  recently_viewed_key?: string;
  /** In-page context for that visit (EL-1065). Ignored server-side without a
   *  `recently_viewed_key` — it describes a visit, so there is nothing to
   *  attach it to on its own. */
  recently_viewed_detail?: HomeRecentlyViewedDetail;
}

// ---------------------------------------------------------------------------
// Statutory compliance obligations (EL-1066)
// ---------------------------------------------------------------------------

/** The statutory instruments Elli tracks a deadline for. */
export type ComplianceReportType = 'rie' | 'psra';

/**
 * How far a tenant has got with one obligation. `not_configured` is the
 * DEFAULT and the common case — most tenants have never recorded a due date,
 * and that must render as "not configured" rather than any countdown.
 */
export type ComplianceStatus = 'not_configured' | 'configured' | 'launched' | 'filed';

/**
 * One statutory obligation as the survey service reports it.
 *
 * `days_remaining` is `null` whenever no `due_date` is stored — the backend
 * omits it rather than inventing a countdown, and it is documented there as
 * "never 0 — zero is the honest answer only for a deadline that really falls
 * today". It can also be NEGATIVE: overdue is a materially different state
 * from due-today and is deliberately not clamped, so never `Math.abs()` it.
 *
 * Nothing in this shape asserts that a tenant IS compliant. It records what
 * they configured, not legal standing.
 */
export interface ComplianceReport {
  tenant_id: string;
  report_type: ComplianceReportType;
  status: ComplianceStatus;
  due_date: string | null;
  days_remaining: number | null;
  linked_survey_id: string | null;
  responsible_name: string | null;
  responsible_email: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ComplianceReportsResponse {
  reports: ComplianceReport[];
}

/**
 * What a tenant can record about one obligation (EL-1066 write side).
 *
 * Deliberately NOT `Partial<ComplianceReport>`: the writable fields are a
 * strict subset. `days_remaining` is derived on every read and must never be
 * sent; `tenant_id`, `created_at` and `updated_at` are the server's to set.
 * Typing the request off the response shape would invite a caller to echo a
 * stale countdown back and have it stored.
 *
 * The endpoint is a PUT — every omitted field is CLEARED, so a caller must
 * send the complete intended state rather than only what changed.
 */
export interface ComplianceReportConfig {
  /** `YYYY-MM-DD`. `null` clears the deadline and returns the obligation to `not_configured`. */
  due_date: string | null;
  linked_survey_id: string | null;
  /** The prevention adviser (preventieadviseur) responsible for the filing. */
  responsible_name: string | null;
  responsible_email: string | null;
  /**
   * Omitted on purpose in most writes: the backend derives `configured` vs
   * `not_configured` from the presence of a due date, and rejects (422) a
   * status that contradicts it. Only send it to record `launched` / `filed`,
   * which are claims the tenant makes and the platform does not verify.
   */
  status?: ComplianceStatus;
}


/**
 * Whether an action card is currently hidden from the tenant's teams.
 *
 * Mirrors the backend's `resolve_tenant_visibility` /
 * `effective_show_to_tenant`, which is a plain Python property and therefore
 * NOT serialised — the two inputs are on the wire, the derived answer is not.
 * Kept as one exported helper so no caller re-implements the precedence and
 * gets it subtly wrong.
 *
 * Defaults match the backend's: an action with neither field present is
 * visible, which is how every card behaved before EL-1064.
 */
export function isHiddenFromTeams(action: {
  show_to_tenant?: boolean;
  tenant_visibility_override?: boolean | null;
}): boolean {
  const override = action.tenant_visibility_override;
  if (override !== null && override !== undefined) return !override;
  return action.show_to_tenant === false;
}
